import type { Role } from '@tkm/domain'

/**
 * What each person's app contains.
 *
 * Derived from role plus the flags the server says this role can see, so the
 * navigation and the gate cannot drift apart - and so a screen is never hidden
 * by a hand-maintained per-person list that someone forgets to update. Hiding a
 * tile here is a courtesy; requireFlag on the route is the actual control.
 */
export interface NavItem {
  id: string
  label: string
  /** The glyph the tile carries. Words plus icon, per the Mary rule. */
  glyph: string
  /** Present when the item exists only while a flag is visible to this role. */
  flag?: string
}

const STATIONS: NavItem[] = [
  { id: 'intake', label: 'Intake', glyph: 'tub' },
  { id: 'catalogue', label: 'Catalogue', glyph: 'book' },
  { id: 'shelve', label: 'Shelve', glyph: 'bin' },
  { id: 'pick', label: 'Pick', glyph: 'list' },
  { id: 'pack', label: 'Pack', glyph: 'box' },
  // The five-second bar. In the station list rather than behind an overflow
  // because a book leaving the shelf is not a station's business - it happens
  // wherever the person is standing, and the cost of it being two taps away is
  // an inventory ledger nobody trusts.
  { id: 'movement', label: 'Take a book', glyph: 'home' },
]

const OWNER_ONLY: NavItem[] = [
  { id: 'drops', label: 'Drops', glyph: 'board' },
  { id: 'claims', label: 'Claims', glyph: 'gavel' },
  { id: 'crm', label: 'Customers', glyph: 'people', flag: 'crm' },
  { id: 'metrics', label: 'Metrics', glyph: 'chart', flag: 'metrics' },
  { id: 'rollout', label: 'Rollout', glyph: 'toggle' },
]

/**
 * Jayant's whole app: one decision each, nothing nested.
 *
 * Supplier payments leads because it is his actual job - it used to lead to a
 * page headed "Approvals" listing shipment retries, which is nobody's job.
 */
const ADMIN_TILES: NavItem[] = [
  { id: 'suppliers', label: 'Supplier payments', glyph: 'rupee' },
  { id: 'inbound', label: 'Coming in', glyph: 'tub' },
  { id: 'approvals', label: 'Approvals', glyph: 'tick' },
  { id: 'metrics', label: 'Metrics', glyph: 'chart', flag: 'metrics' },
  { id: 'day', label: 'Today', glyph: 'sun' },
]

export function navigationFor(role: Role, visibleFlags: readonly string[]): NavItem[] {
  const allowed = (item: NavItem) => !item.flag || visibleFlags.includes(item.flag)
  if (role === 'owner') return [...STATIONS, ...OWNER_ONLY].filter(allowed)
  if (role === 'admin') return ADMIN_TILES.filter(allowed)
  if (role === 'fulfilment') return STATIONS.filter((s) => s.id !== 'intake').filter(allowed)
  return STATIONS.filter(allowed)
}
